<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { RouterView, useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { ConfirmDialog, ProgressSpinner } from "primevue";
import { Toast } from "primevue";
import { useI18n } from "vue-i18n";
import { setI18nLocale } from "@/composables/useLanguage";
import HeaderSingle from "./singles/HeaderSingle.vue";
import { Toaster } from "vue-sonner";
import LiquidGradient from "@/components/layout/LiquidGradient.vue";

const store = useStore();
const { locale } = useI18n();
const authReady = computed(() => store.state.auth.initialized);
const route = useRoute();
const router = useRouter();

const loggedIn = computed(() => store.getters["auth/isLoggedIn"]);
const loginViewIsLeaving = ref(false);
const songOrSermonPathRegex =
  /^\/event\/[^/]+\/schedule\/[^/]+\/(song|sermon)\/[^/]+$/;
const pathImpliesHiddenHeader = computed(() =>
  songOrSermonPathRegex.test(route.path),
);
const bootPathImpliesHiddenHeader = ref(
  typeof window !== "undefined"
    ? songOrSermonPathRegex.test(window.location.pathname)
    : false,
);
const shouldHideHeader = computed(
  () =>
    bootPathImpliesHiddenHeader.value ||
    Boolean(route.meta.hideHeader) ||
    pathImpliesHiddenHeader.value,
);
const routeTransitionName = computed(() =>
  songOrSermonPathRegex.test(route.path) ? "route-none" : "fade",
);
const routeViewKey = computed(() => {
  if (route.name === "event-song") return `event-song-${route.params.id}`;
  if (route.name === "event-sermon") return `event-sermon-${route.params.id}`;
  // The repertoire keeps its page in the query; turning it must not remount.
  if (route.name === "repertoire") return "repertoire";
  return route.fullPath;
});
const suppressInitialHeaderTransition = ref(true);
const headerTransitionClass = computed(() =>
  suppressInitialHeaderTransition.value
    ? "transition-none duration-0 delay-0"
    : "transition-all ease-[cubic-bezier(.4,0,.2,1)] duration-500 delay-300",
);
const shouldShowHeader = computed(
  () => loggedIn.value && route.name !== "login" && !loginViewIsLeaving.value,
);
const shouldShowSharedBackground = computed(
  () =>
    route.name === "login" ||
    route.name === "home" ||
    loginViewIsLeaving.value,
);

const popupMode = computed(() => {
  return store.getters.popupMode;
});

watch(
  () => route.name,
  (_to, from) => {
    if (from === "login") loginViewIsLeaving.value = true;
  },
);

const onRouteAfterLeave = () => {
  loginViewIsLeaving.value = false;
};

onMounted(() => {
  const isDarkMode = localStorage.getItem("darkMode");

  setI18nLocale(locale.value);

  if (isDarkMode && isDarkMode == "true") {
    store.dispatch("setDarkMode", { darkMode: true });
  }

  router.isReady().then(() => {
    requestAnimationFrame(() => {
      bootPathImpliesHiddenHeader.value = false;
      suppressInitialHeaderTransition.value = false;
    });
  });
});
</script>

<template>
  <div
    v-if="shouldShowSharedBackground"
    class="fixed inset-0 z-0 overflow-hidden pointer-events-none"
  >
    <LiquidGradient />
  </div>
  <div
    v-if="!authReady"
    class="relative z-10 flex items-center justify-center h-screen"
  >
    <ProgressSpinner />
  </div>
  <div
    v-else
    class="relative z-10 flex flex-col h-full"
    :class="{ fixed: popupMode == true }"
  >
    <HeaderSingle
      v-if="shouldShowHeader"
      :class="[
        headerTransitionClass,
        { 'opacity-0 -mt-20 pointer-events-none': shouldHideHeader },
      ]"
    />
    <RouterView v-slot="{ Component }" class="grow">
      <Transition
        :name="routeTransitionName"
        mode="out-in"
        appear
        @after-leave="onRouteAfterLeave"
      >
        <component :is="Component" :key="routeViewKey" />
      </Transition>
    </RouterView>
  </div>
  <Toaster richColors />
  <Toast />
  <ConfirmDialog
    :pt="{
      root: { class: 'mx-4 sm:mx-auto' },
      content: { class: '!px-4 !pb-4' },
      footer: { class: '!py-2 !px-4' },
    }"
  />
</template>

<style lang="scss">
.my-confirm-root {
  @apply w-[520px] max-w-[90vw];
}

.my-confirm-content {
  @apply px-8 py-6;
}

.route-none-enter-active,
.route-none-leave-active {
  transition: none;
}
</style>
